import type {
  IStatusPanelComp,
  IStatusPanelParams,
  GridApi,
} from "ag-grid-enterprise";

export interface SelectedRowCountStatusPanelParams extends IStatusPanelParams {
  /** Label shown before the count, e.g. "Voters Selected" or "Selected". Defaults to "Voters Selected". */
  label?: string;
}

/**
 * Custom replacement for AG Grid's built-in `agSelectedRowCountComponent`, matching its
 * markup/behavior (label, hide-when-empty). Kept as our own component so the wording can
 * diverge later without fighting the built-in's `localeText` constraints.
 *
 * Label is configurable via `statusPanelParams: { label: '...' }` on the statusPanel def.
 */
export class SelectedRowCountStatusPanel implements IStatusPanelComp {
  private eGui!: HTMLElement;
  private eNameValueWrapper!: HTMLElement;
  private eLabel!: HTMLElement;
  private eValue!: HTMLElement;
  private eMessage!: HTMLElement;
  private api!: GridApi;
  private onSelectionChanged = () => this.refreshValue();

  init(params: SelectedRowCountStatusPanelParams): void {
    this.api = params.api;

    this.eGui = document.createElement("div");
    this.eGui.className = "ag-status-name-value ag-status-panel ag-status-panel-selected-row-count";

    this.eNameValueWrapper = document.createElement("span");
    this.eLabel = document.createElement("span");
    this.eNameValueWrapper.appendChild(this.eLabel);
    // Regular trailing space gets trimmed here — the panel is display:flex, and a plain
    // space at the edge of an anonymous flex item's isolated line box collapses away.
    this.eNameValueWrapper.appendChild(document.createTextNode(": "));

    this.eValue = document.createElement("span");
    this.eValue.className = "ag-status-name-value-value";
    this.eNameValueWrapper.appendChild(this.eValue);
    this.eGui.appendChild(this.eNameValueWrapper);

    // Select-all only covers rows loaded so far, not the whole filtered set — swapped in
    // for the label/value pair while active instead of a plain "Voters Selected: N".
    this.eMessage = document.createElement("span");
    this.eMessage.className = "ag-status-panel-selected-row-count__message";
    this.eGui.appendChild(this.eMessage);

    this.api.addEventListener("selectionChanged", this.onSelectionChanged);
    this.api.addEventListener("modelUpdated", this.onSelectionChanged);
    this.refresh(params);
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(params: SelectedRowCountStatusPanelParams): boolean {
    this.eLabel.textContent = params.label ?? "Voters Selected";
    this.refreshValue();
    return true;
  }

  destroy(): void {
    this.api.removeEventListener("selectionChanged", this.onSelectionChanged);
    this.api.removeEventListener("modelUpdated", this.onSelectionChanged);
  }

  private refreshValue(): void {
    const count = this.getSelectedCount();
    const showWarning = this.isSelectAllActive() && count > 0;

    if (showWarning) {
      this.eNameValueWrapper.classList.add("ag-hidden");
      this.eMessage.classList.remove("ag-hidden");
      this.eMessage.textContent =
        `The first ${count} records are selected, alternatively, before selecting, filter the list.`;
    } else {
      this.eMessage.classList.add("ag-hidden");
      this.eNameValueWrapper.classList.remove("ag-hidden");
      this.eValue.textContent = String(count);
    }

    this.eGui.classList.toggle("ag-status-panel-selected-row-count--warning", showWarning);
    this.eGui.classList.toggle("ag-hidden", count === 0);
    this.eGui.setAttribute("aria-hidden", String(count === 0));
  }

  private isSelectAllActive(): boolean {
    const selectionState = this.api.getServerSideSelectionState() as { selectAll?: boolean } | null;
    return selectionState?.selectAll === true;
  }

  private getSelectedCount(): number {
    let count = 0;
    const displayedCount = this.api.getDisplayedRowCount();
    for (let i = 0; i < displayedCount; i++) {
      const node = this.api.getDisplayedRowAtIndex(i);
      if (!node || node.data === undefined) break; // stop at the loaded/unloaded boundary
      if (node.group) continue;
      if (node.isSelected()) count++;
    }
    return count;
  }
}
